// H-017 — banda de identidad de la ficha con «privacidad de pantalla» y revelado auditado (ver lib/vistaMinimizada.ts).
// Es protección de PANTALLA, no del dato: el recurso FHIR del paciente sigue llegando completo al navegador. Apagada por defecto (activarla no revela nada,
// así que no deja registro). Encendida: iniciales, edad y género, identificación enmascarada. Para ver el dato completo, sea «Revelar datos» o salir de la
// privacidad de pantalla, se pide un motivo y el servidor deja la constancia (exige vínculo asistencial); SOLO entonces se muestra. «Revelar» dura 5
// minutos; al vencer o al cambiar de paciente vuelve a ocultarse.
import { Eye, EyeOff } from 'lucide-react';
import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  DURACION_REVELADO_MS,
  guardarPreferenciaMinimizada,
  identificacionMinima,
  leerPreferenciaMinimizada,
  MOTIVOS_REVELADO,
  solicitarRevelado,
  type CampoRevelable,
  type MotivoRevelado,
} from '@/lib/vistaMinimizada';

export interface IdentidadMinimizableProps {
  readonly pacienteId: string;
  /** Nombre completo (solo se pinta si la vista no está minimizada o ya se reveló). */
  readonly nombre: string;
  /** Iniciales ya calculadas (lo único del nombre que se ve minimizado). */
  readonly iniciales: string;
  /** `65 M` (edad y género): dato mínimo que siempre se ve. */
  readonly edadGenero?: string;
  readonly nacimiento?: string;
  readonly identificacion?: string;
  /** Insignias que van junto al nombre (archivo, limitación). */
  readonly children?: ReactNode;
}

const CAMPOS: readonly CampoRevelable[] = ['nombre', 'identificacion', 'nacimiento'];

export function IdentidadMinimizable({ pacienteId, nombre, iniciales, edadGenero, nacimiento, identificacion, children }: IdentidadMinimizableProps): JSX.Element {
  const { t } = useTranslation();
  const [minimizada, setMinimizada] = useState(leerPreferenciaMinimizada);
  const [revelado, setRevelado] = useState(false);
  const [dialogo, setDialogo] = useState<'revelar' | 'salir' | null>(null);
  const [motivo, setMotivo] = useState<MotivoRevelado | null>(null);
  const [enviando, setEnviando] = useState(false);
  const [error, setError] = useState<string | null>(null);

  // El revelado es de ESTE paciente: al abrir otro, o pasado el tiempo, vuelve a ocultarse.
  useEffect(() => setRevelado(false), [pacienteId]);
  useEffect(() => {
    if (!revelado) return undefined;
    const id = setTimeout(() => setRevelado(false), DURACION_REVELADO_MS);
    return () => clearTimeout(id);
  }, [revelado]);

  const oculta = minimizada && !revelado;

  // Encender no revela nada (sin constancia); apagar es ver el dato completo: pasa por el mismo diálogo y la misma constancia.
  function alternar(): void {
    if (!minimizada) {
      setMinimizada(true);
      setRevelado(false);
      guardarPreferenciaMinimizada(true);
      return;
    }
    setDialogo('salir');
  }

  async function confirmar(): Promise<void> {
    if (!motivo || enviando) return;
    setEnviando(true);
    setError(null);
    const r = await solicitarRevelado(pacienteId, CAMPOS, motivo);
    setEnviando(false);
    if (!r.ok) {
      setError(r.error);
      return;
    }
    if (dialogo === 'salir') {
      setMinimizada(false);
      guardarPreferenciaMinimizada(false);
    }
    setDialogo(null);
    setMotivo(null);
    setRevelado(true);
  }

  const partes = [
    edadGenero,
    !oculta && nacimiento ? `${t('fichaPaciente.nacimiento')} ${nacimiento}` : undefined,
    identificacion ? `${t('fichaPaciente.identificacion')} ${oculta ? identificacionMinima(identificacion) : identificacion}` : undefined,
  ].filter(Boolean);

  return (
    <>
      <div className="flex flex-wrap items-baseline gap-3">
        <h1 className="text-lg font-medium">{oculta ? `${iniciales.split('').join('. ')}.` : nombre}</h1>
        {partes.length > 0 && <span className="font-mono text-xs text-muted-foreground">{partes.join(' · ')}</span>}
        {children}
        <Button type="button" variant="ghost" size="sm" onClick={alternar} aria-pressed={minimizada} className="h-6 gap-1 px-2 text-xs">
          {minimizada ? <Eye className="size-3" /> : <EyeOff className="size-3" />}
          {minimizada ? t('fichaPaciente.minimizada.mostrar') : t('fichaPaciente.minimizada.minimizar')}
        </Button>
        {oculta && (
          <Button type="button" variant="outline" size="sm" onClick={() => setDialogo('revelar')} className="h-6 px-2 text-xs">
            {t('fichaPaciente.minimizada.revelar')}
          </Button>
        )}
      </div>
      <Dialog open={dialogo !== null} onOpenChange={(abierto) => !enviando && !abierto && setDialogo(null)}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{t('fichaPaciente.minimizada.titulo')}</DialogTitle>
            <DialogDescription>{t('fichaPaciente.minimizada.descripcion')}</DialogDescription>
          </DialogHeader>
          <fieldset className="space-y-2" disabled={enviando}>
            <legend className="sr-only">{t('fichaPaciente.minimizada.motivo')}</legend>
            {MOTIVOS_REVELADO.map((m) => (
              <label key={m} className="flex items-center gap-2 text-sm">
                <input type="radio" name="motivo-revelado" value={m} checked={motivo === m} onChange={() => setMotivo(m)} />
                {t(`fichaPaciente.minimizada.motivos.${m}`)}
              </label>
            ))}
          </fieldset>
          {error && (
            <p role="alert" className="text-sm text-destructive">
              {error}
            </p>
          )}
          <DialogFooter>
            <Button type="button" variant="outline" onClick={() => setDialogo(null)} disabled={enviando}>
              {t('fichaPaciente.minimizada.cancelar')}
            </Button>
            <Button type="button" onClick={() => void confirmar()} disabled={!motivo || enviando}>
              {t('fichaPaciente.minimizada.confirmar')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
